// src/components/AdminAlertsPanel.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { ChevronDown, ChevronUp } from "lucide-react";

/* ================== AdminAlertsPanel (mobile responsive) ================== */
function AdminAlertsPanel() {
  const { getToken } = useAuth();

  const ALERTS_API_BASE =
    process.env.REACT_APP_API_URL ||
    (typeof window !== "undefined" && window._API_BASE) ||
    "https://ecom-ads-ai-production.up.railway.app";

    const TOPVIEW_KEYS_BASE = `${ALERTS_API_BASE}/api/admin/topview-keys`;
    const TOPVIEW_KEY_RUNTIME_BASE = `${ALERTS_API_BASE}/api/admin/topview-keys`;
    const PLACID_USAGE_BASE = `${ALERTS_API_BASE}/api/admin/alerts/vendors/placid/usage-config`;

  const ALERTS_CARD = "#141428";
  const ALERTS_BORDER = "#2A2A40";

  // ✅ FIX: stabilize presets + helpers so useMemo deps are satisfied (and stable)
  const FREQ_PRESETS = useMemo(
    () => [
      { id: "1h", label: "Every 1 hour", minutes: 60 },
      { id: "2h", label: "Every 2 hours", minutes: 120 },
      { id: "3h", label: "Every 3 hours", minutes: 180 },
      { id: "6h", label: "Every 6 hours", minutes: 360 },
      { id: "12h", label: "Every 12 hours", minutes: 720 },
      { id: "24h", label: "Every 24 hours", minutes: 1440 },
    ],
    []
  );

  const minutesToChoice = useCallback(
    (minutes) => {
      const n = parseInt(minutes, 10) || 720;
      const preset = FREQ_PRESETS.find((p) => p.minutes === n);
      return preset ? preset.id : "12h";
    },
    [FREQ_PRESETS]
  );

  const choiceToMinutes = useCallback(
    (id) => {
      const preset = FREQ_PRESETS.find((p) => p.id === id);
      return preset?.minutes || 720;
    },
    [FREQ_PRESETS]
  );

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [cfg, setCfg] = useState(null);

  const [vPhot, setVPhot] = useState(null);
  const [vTopview, setVTopview] = useState(null);
  const [vPlacidVendor, setVPlacidVendor] = useState(null);
  const [vSendgrid, setVSendgrid] = useState(null);
  const [vCloudinary, setVCloudinary] = useState(null); // ✅ NEW

  const [topviewKeys, setTopviewKeys] = useState([]);
  const [newTopviewKey, setNewTopviewKey] = useState({
    label: "",
    uid: "",
    api_key: "",
    monthly_quota: "",
    enabled: true,
  });

  const [recipients, setRecipients] = useState({
    from: "",
    to: [],
    cc: [],
  });
  const [newEmail, setNewEmail] = useState("");

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const token = await getToken();

      // Global config
      const r1 = await fetch(`${ALERTS_API_BASE}/api/admin/alerts/config`, {
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
      });
      const c = await r1.json();
      if (!r1.ok) throw new Error(c?.error || "Failed to load alerts config");
      setCfg(c || {});

      /* ========== Phot.AI vendor config + live credits ========== */
      const r2 = await fetch(`${ALERTS_API_BASE}/api/admin/alerts/vendors/photai`, {
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
      });
      const p = await r2.json();

      let liveRemaining = null;
      let liveTotal = null;
      let lastChecked = null;
      try {
        const r3 = await fetch(`${ALERTS_API_BASE}/api/admin/alerts/vendors/photai/credits`, {
          headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
        });
        const creditsJson = await r3.json();
        if (r3.ok && creditsJson && creditsJson.ok) {
          liveRemaining =
            typeof creditsJson.remaining === "number" ? creditsJson.remaining : null;
          liveTotal = typeof creditsJson.total === "number" ? creditsJson.total : null;
          lastChecked = new Date().toISOString();
        }
      } catch {
        // ignore
      }

      setVPhot(
        p && typeof p === "object"
          ? {
              enabled: p.enabled ?? true,
              silenced: p.silenced ?? false,
              threshold_count: p.threshold_count ?? 20,
              frequency_minutes: p.frequency_minutes ?? 720,
              remaining_credits:
                liveRemaining ?? p.remaining_credits ?? p.last_notified?.remaining ?? null,
              credits_cap: liveTotal ?? p.credits_cap ?? null,
              last_checked_at: lastChecked ?? p.last_checked_at ?? p.last_notified?.date ?? null,
            }
          : {
              enabled: true,
              silenced: false,
              threshold_count: 20,
              frequency_minutes: 720,
              remaining_credits: liveRemaining,
              credits_cap: liveTotal,
              last_checked_at: lastChecked,
            }
      );

      /* ========== TopView vendor config + live credits ========== */
      const rTop = await fetch(`${ALERTS_API_BASE}/api/admin/alerts/vendors/topview`, {
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
      });
      const t = await rTop.json();

      let tvRemaining = null;
      let tvTotal = null;
      let tvLastChecked = null;
      try {
        const rTv = await fetch(`${ALERTS_API_BASE}/api/admin/alerts/vendors/topview/credits`, {
          headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
        });
        const tvJson = await rTv.json();
        if (rTv.ok && tvJson && tvJson.ok) {
          tvRemaining = typeof tvJson.remaining === "number" ? tvJson.remaining : null;
          tvTotal = typeof tvJson.total === "number" ? tvJson.total : null;
          tvLastChecked = new Date().toISOString();
        }
      } catch {
        // ignore
      }

      setVTopview(
        t && typeof t === "object"
          ? {
              enabled: t.enabled ?? true,
              silenced: t.silenced ?? false,
              threshold_count: t.threshold_count ?? 20,
              frequency_minutes: t.frequency_minutes ?? 720,
              remaining_credits:
                tvRemaining ?? t.remaining_credits ?? t.last_notified?.remaining ?? null,
              credits_cap: tvTotal ?? t.credits_cap ?? null,
              last_checked_at: tvLastChecked ?? t.last_checked_at ?? t.last_notified?.date ?? null,
            }
          : {
              enabled: true,
              silenced: false,
              threshold_count: 20,
              frequency_minutes: 720,
              remaining_credits: tvRemaining,
              credits_cap: tvTotal,
              last_checked_at: tvLastChecked,
            }
      );

      /* ========== Placid vendor config + live credits + monthly limit ========== */
      try {
        const rPlVendor = await fetch(`${ALERTS_API_BASE}/api/admin/alerts/vendors/placid`, {
          headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
        });
        const plCfg = await rPlVendor.json();

        let plRemaining = null;
        let plTotal = null;
        let plLastChecked = null;
        let plMonthlyLimit = null;

        try {
          const rPlCred = await fetch(`${ALERTS_API_BASE}/api/admin/alerts/vendors/placid/credits`, {
            headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
          });
          const plCredJson = await rPlCred.json();
          if (rPlCred.ok && plCredJson && plCredJson.ok) {
            plRemaining =
              typeof plCredJson.remaining === "number" ? plCredJson.remaining : null;
            plTotal = typeof plCredJson.total === "number" ? plCredJson.total : null;
            plLastChecked = new Date().toISOString();

            const usage = plCredJson.payload?.usage;
            if (usage && typeof usage === "object") {
              plMonthlyLimit = typeof usage.monthly_limit === "number" ? usage.monthly_limit : null;
            }
          }
        } catch {
          // ignore
        }

        try {
          const rUsage = await fetch(PLACID_USAGE_BASE, {
            headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
          });
          const uJson = await rUsage.json();
          if (rUsage.ok && uJson && typeof uJson === "object") {
            if (plMonthlyLimit == null && typeof uJson.monthly_limit === "number") {
              plMonthlyLimit = uJson.monthly_limit;
            }
          }
        } catch {
          // ignore
        }

        setVPlacidVendor(
          plCfg && typeof plCfg === "object"
            ? {
                enabled: plCfg.enabled ?? true,
                silenced: plCfg.silenced ?? false,
                threshold_count: plCfg.threshold_count ?? 20,
                frequency_minutes: plCfg.frequency_minutes ?? 720,
                remaining_credits:
                  plRemaining ?? plCfg.remaining_credits ?? plCfg.last_notified?.remaining ?? null,
                credits_cap: plTotal ?? plCfg.credits_cap ?? null,
                last_checked_at:
                  plLastChecked ?? plCfg.last_checked_at ?? plCfg.last_notified?.date ?? null,
                monthly_limit: plMonthlyLimit ?? null,
              }
            : {
                enabled: true,
                silenced: false,
                threshold_count: 20,
                frequency_minutes: 720,
                remaining_credits: plRemaining,
                credits_cap: plTotal,
                last_checked_at: plLastChecked,
                monthly_limit: plMonthlyLimit ?? null,
              }
        );
      } catch {
        setVPlacidVendor(null);
      }

      /* ========== SendGrid vendor config + live credits ========== */
      try {
        const rSgVendor = await fetch(`${ALERTS_API_BASE}/api/admin/alerts/vendors/sendgrid`, {
          headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
        });
        const sgCfg = await rSgVendor.json();

        let sgRemaining = null;
        let sgTotal = null;
        let sgLastChecked = null;

        try {
          const rSgCred = await fetch(`${ALERTS_API_BASE}/api/admin/alerts/vendors/sendgrid/credits`, {
            headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
          });
          const sgCredJson = await rSgCred.json();
          if (rSgCred.ok && sgCredJson && sgCredJson.ok) {
            sgRemaining =
              typeof sgCredJson.remaining === "number" ? sgCredJson.remaining : null;
            sgTotal = typeof sgCredJson.total === "number" ? sgCredJson.total : null;
            sgLastChecked = new Date().toISOString();
          }
        } catch {
          // ignore
        }

        setVSendgrid(
          sgCfg && typeof sgCfg === "object"
            ? {
                enabled: sgCfg.enabled ?? true,
                silenced: sgCfg.silenced ?? false,
                threshold_count: sgCfg.threshold_count ?? 200,
                frequency_minutes: sgCfg.frequency_minutes ?? 720,
                remaining_credits:
                  sgRemaining ??
                  sgCfg.remaining_credits ??
                  sgCfg.last_notified?.remaining ??
                  null,
                credits_cap: sgTotal ?? sgCfg.credits_cap ?? null,
                last_checked_at:
                  sgLastChecked ?? sgCfg.last_checked_at ?? sgCfg.last_notified?.date ?? null,
              }
            : {
                enabled: true,
                silenced: false,
                threshold_count: 200,
                frequency_minutes: 720,
                remaining_credits: sgRemaining,
                credits_cap: sgTotal,
                last_checked_at: sgLastChecked,
              }
        );
      } catch {
        setVSendgrid(null);
      }

      /* ========== Cloudinary vendor config + live credits ========== */
      try {
        const rClVendor = await fetch(`${ALERTS_API_BASE}/api/admin/alerts/vendors/cloudinary`, {
          headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
        });
        const clCfg = await rClVendor.json();

        let clRemaining = null;
        let clTotal = null;
        let clLastChecked = null;

        try {
          const rClCred = await fetch(
            `${ALERTS_API_BASE}/api/admin/alerts/vendors/cloudinary/credits`,
            {
              headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
            }
          );
          const clCredJson = await rClCred.json();
          if (rClCred.ok && clCredJson && clCredJson.ok) {
            clRemaining =
              typeof clCredJson.remaining === "number" ? clCredJson.remaining : null;
            clTotal = typeof clCredJson.total === "number" ? clCredJson.total : null;
            clLastChecked = new Date().toISOString();
          }
        } catch {
          // ignore
        }

        setVCloudinary(
          clCfg && typeof clCfg === "object"
            ? {
                enabled: clCfg.enabled ?? true,
                silenced: clCfg.silenced ?? false,
                threshold_count: clCfg.threshold_count ?? 1000,
                frequency_minutes: clCfg.frequency_minutes ?? 720,
                remaining_credits:
                  clRemaining ??
                  clCfg.remaining_credits ??
                  clCfg.last_notified?.remaining ??
                  null,
                credits_cap: clTotal ?? clCfg.credits_cap ?? null,
                last_checked_at:
                  clLastChecked ?? clCfg.last_checked_at ?? clCfg.last_notified?.date ?? null,
              }
            : {
                enabled: true,
                silenced: false,
                threshold_count: 1000,
                frequency_minutes: 720,
                remaining_credits: clRemaining,
                credits_cap: clTotal,
                last_checked_at: clLastChecked,
              }
        );
      } catch {
        setVCloudinary(null);
      }

      /* ========== TopView keys pool ========== */
      try {
        const rKeys = await fetch(TOPVIEW_KEYS_BASE, {
          headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
        });
        const keysJson = await rKeys.json();

        let baseKeys;
        if (rKeys.ok && keysJson && Array.isArray(keysJson.keys)) {
          baseKeys = keysJson.keys;
        } else if (Array.isArray(keysJson)) {
          baseKeys = keysJson;
        } else {
          baseKeys = [];
        }

        baseKeys = (baseKeys || []).map((k) => ({ ...k, last_error: null, live_error: null }));

        let tvPerKey = null;
        try {
          const rTv = await fetch(`${ALERTS_API_BASE}/api/admin/alerts/vendors/topview/credits`, {
            headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
          });
          const tvJson = await rTv.json();
          if (rTv.ok && tvJson && tvJson.ok && Array.isArray(tvJson.perKey)) {
            tvPerKey = tvJson.perKey;
          }
        } catch {
          // ignore
        }

        if (tvPerKey && tvPerKey.length > 0 && baseKeys.length > 0) {
          const merged = baseKeys.map((k) => {
            const live = tvPerKey.find((x) => x.id === k.id);
            if (!live) return k;
            return {
              ...k,
              live_remaining_credits:
                typeof live.remaining_credits === "number"
                  ? live.remaining_credits
                  : k.live_remaining_credits,
              live_expired_time: live.expired_time,
              live_error: null,
              last_error: null,
            };
          });
          setTopviewKeys(merged);
        } else {
          setTopviewKeys(baseKeys);
        }
      } catch {
        setTopviewKeys([]);
      }

      /* ========== Recipients config ========== */
      const r4 = await fetch(`${ALERTS_API_BASE}/api/admin/alerts/recipients`, {
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
      });
      const recJson = await r4.json();
      if (!r4.ok) throw new Error(recJson?.error || "Failed to load recipients");
      setRecipients({
        from: recJson.from || "",
        to: Array.isArray(recJson.to) ? recJson.to : [],
        cc: Array.isArray(recJson.cc) ? recJson.cc : [],
      });
    } catch (e) {
      setErr(e.message || "Load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && cfg == null && !loading) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const savePhot = async () => {
    if (!vPhot) return;
    setSaving(true);
    setErr("");
    try {
      const token = await getToken();
      const res = await fetch(`${ALERTS_API_BASE}/api/admin/alerts/vendors/photai`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          enabled: !!vPhot.enabled,
          silenced: !!vPhot.silenced,
          threshold_count: parseInt(vPhot.threshold_count || 0, 10),
          frequency_minutes: vPhot.frequency_minutes ?? 720,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Save failed");
      setVPhot((prev) => ({
        ...(prev || {}),
        ...(json.config || {}),
        frequency_minutes: json.config?.frequency_minutes ?? prev?.frequency_minutes ?? 720,
      }));
    } catch (e) {
      setErr(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const saveTopview = async () => {
    if (!vTopview) return;
    setSaving(true);
    setErr("");
    try {
      const token = await getToken();
      const res = await fetch(`${ALERTS_API_BASE}/api/admin/alerts/vendors/topview`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          enabled: !!vTopview.enabled,
          silenced: !!vTopview.silenced,
          threshold_count: parseInt(vTopview.threshold_count || 0, 10),
          frequency_minutes: vTopview.frequency_minutes ?? 720,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Save failed");
      setVTopview((prev) => ({
        ...(prev || {}),
        ...(json.config || {}),
        frequency_minutes: json.config?.frequency_minutes ?? prev?.frequency_minutes ?? 720,
      }));
    } catch (e) {
      setErr(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const savePlacidVendor = async () => {
    if (!vPlacidVendor) return;
    setSaving(true);
    setErr("");
    try {
      const token = await getToken();

      const res = await fetch(`${ALERTS_API_BASE}/api/admin/alerts/vendors/placid`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          enabled: !!vPlacidVendor.enabled,
          silenced: !!vPlacidVendor.silenced,
          threshold_count: parseInt(vPlacidVendor.threshold_count || 0, 10),
          frequency_minutes: vPlacidVendor.frequency_minutes ?? 720,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Save failed");

      const usageRes = await fetch(PLACID_USAGE_BASE, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          monthly_limit: vPlacidVendor.monthly_limit ?? 0,
        }),
      });
      const usageJson = await usageRes.json();
      if (!usageRes.ok)
        throw new Error(usageJson?.error || "Failed to save Placid usage config");

      setVPlacidVendor((prev) => ({
        ...(prev || {}),
        ...(json.config || {}),
        monthly_limit:
          usageJson.config?.monthly_limit ??
          prev?.monthly_limit ??
          vPlacidVendor.monthly_limit ??
          null,
        credits_cap:
          usageJson.config?.monthly_limit ??
          prev?.credits_cap ??
          vPlacidVendor.credits_cap ??
          null,
        frequency_minutes: json.config?.frequency_minutes ?? prev?.frequency_minutes ?? 720,
      }));
    } catch (e) {
      setErr(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const saveSendgrid = async () => {
    if (!vSendgrid) return;
    setSaving(true);
    setErr("");
    try {
      const token = await getToken();
      const res = await fetch(`${ALERTS_API_BASE}/api/admin/alerts/vendors/sendgrid`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          enabled: !!vSendgrid.enabled,
          silenced: !!vSendgrid.silenced,
          threshold_count: parseInt(vSendgrid.threshold_count || 0, 10),
          frequency_minutes: vSendgrid.frequency_minutes ?? 720,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Save failed");
      setVSendgrid((prev) => ({
        ...(prev || {}),
        ...(json.config || {}),
        frequency_minutes: json.config?.frequency_minutes ?? prev?.frequency_minutes ?? 720,
      }));
    } catch (e) {
      setErr(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const saveCloudinary = async () => {
    if (!vCloudinary) return;
    setSaving(true);
    setErr("");
    try {
      const token = await getToken();
      const res = await fetch(`${ALERTS_API_BASE}/api/admin/alerts/vendors/cloudinary`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          enabled: !!vCloudinary.enabled,
          silenced: !!vCloudinary.silenced,
          threshold_count: parseInt(vCloudinary.threshold_count || 0, 10),
          frequency_minutes: vCloudinary.frequency_minutes ?? 720,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Save failed");
      setVCloudinary((prev) => ({
        ...(prev || {}),
        ...(json.config || {}),
        frequency_minutes: json.config?.frequency_minutes ?? prev?.frequency_minutes ?? 720,
      }));
    } catch (e) {
      setErr(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const sendPhotTest = async () => {
    setSaving(true);
    setErr("");
    try {
      const token = await getToken();
      const res = await fetch(`${ALERTS_API_BASE}/api/admin/alerts/test`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ vendor: "photai" }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Phot.AI test failed");
      // eslint-disable-next-line no-alert
      alert("Phot.AI test email sent (includes remaining credits).");
    } catch (e) {
      setErr(e.message || "Phot.AI test failed");
    } finally {
      setSaving(false);
    }
  };

  const sendTopviewTest = async () => {
    setSaving(true);
    setErr("");
    try {
      const token = await getToken();
      const res = await fetch(`${ALERTS_API_BASE}/api/admin/alerts/test`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ vendor: "topview" }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "TopView test failed");
      // eslint-disable-next-line no-alert
      alert("TopView test email sent (includes remaining credits).");
    } catch (e) {
      setErr(e.message || "TopView test failed");
    } finally {
      setSaving(false);
    }
  };

  const sendPlacidTest = async () => {
    setSaving(true);
    setErr("");
    try {
      const token = await getToken();
      const res = await fetch(`${ALERTS_API_BASE}/api/admin/alerts/test`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ vendor: "placid" }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Placid test failed");
      // eslint-disable-next-line no-alert
      alert("Placid test email sent (includes remaining credits).");
    } catch (e) {
      setErr(e.message || "Placid test failed");
    } finally {
      setSaving(false);
    }
  };

  const sendSendgridTest = async () => {
    setSaving(true);
    setErr("");
    try {
      const token = await getToken();
      const res = await fetch(`${ALERTS_API_BASE}/api/admin/alerts/test`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ vendor: "sendgrid" }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "SendGrid test failed");
      // eslint-disable-next-line no-alert
      alert("SendGrid test email sent (includes remaining credits).");
    } catch (e) {
      setErr(e.message || "SendGrid test failed");
    } finally {
      setSaving(false);
    }
  };

  const sendCloudinaryTest = async () => {
    setSaving(true);
    setErr("");
    try {
      const token = await getToken();
      const res = await fetch(`${ALERTS_API_BASE}/api/admin/alerts/test`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ vendor: "cloudinary" }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Cloudinary test failed");
      // eslint-disable-next-line no-alert
      alert("Cloudinary test email sent (includes remaining credits).");
    } catch (e) {
      setErr(e.message || "Cloudinary test failed");
    } finally {
      setSaving(false);
    }
  };

  const addTopviewKey = async () => {
    const label = newTopviewKey.label.trim();
    const uid = newTopviewKey.uid.trim();
    const apiKey = newTopviewKey.api_key.trim();
    const quotaNum = parseInt(String(newTopviewKey.monthly_quota || "").replace(/\D/g, ""), 10);

    if (!label || !uid || !apiKey) {
      setErr("Please fill label, UID and API key for the new TopView key.");
      return;
    }

    setSaving(true);
    setErr("");
    try {
      const token = await getToken();
      const res = await fetch(TOPVIEW_KEYS_BASE, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          label,
          uid,
          api_key: apiKey,
          monthly_quota: Number.isFinite(quotaNum) ? quotaNum : null,
          enabled: !!newTopviewKey.enabled,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Failed to add TopView key");

      const created = json.key || json;
      setTopviewKeys((prev) => [
        ...(prev || []),
        { ...created, last_error: null, live_error: null },
      ]);

      setNewTopviewKey({
        label: "",
        uid: "",
        api_key: "",
        monthly_quota: "",
        enabled: true,
      });
    } catch (e) {
      setErr(e.message || "Failed to add TopView key");
    } finally {
      setSaving(false);
    }
  };

  const toggleTopviewKeyEnabled = async (id, enabled) => {
    setSaving(true);
    setErr("");
    try {
      const token = await getToken();
      const res = await fetch(`${TOPVIEW_KEYS_BASE}/${id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ enabled }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Failed to update key");

      const updated = json.key || json;
      setTopviewKeys((prev) =>
        (prev || []).map((k) =>
          k.id === id ? { ...k, ...updated, last_error: null, live_error: null } : k
        )
      );
    } catch (e) {
      setErr(e.message || "Failed to update key");
    } finally {
      setSaving(false);
    }
  };

  const deleteTopviewKey = async (id) => {
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm("Delete this TopView key?");
    if (!confirmed) return;

    setSaving(true);
    setErr("");
    try {
      const token = await getToken();
      const res = await fetch(`${TOPVIEW_KEYS_BASE}/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
      });
      if (!res.ok) {
        const json = await res.json().catch(() => null);
        throw new Error(json?.error || "Failed to delete key");
      }
      setTopviewKeys((prev) => (prev || []).filter((k) => k.id !== id));
    } catch (e) {
      setErr(e.message || "Failed to delete key");
    } finally {
      setSaving(false);
    }
  };
  const patchTopviewKeyLocal = (id, patch) => {
    setTopviewKeys((prev) =>
      (prev || []).map((k) => (k.id === id ? { ...k, ...patch } : k))
    );
  };
  
  const saveTopviewKeyRuntime = async (keyRow) => {
    if (!keyRow?.id) return;
  
    setSaving(true);
    setErr("");
    try {
      const token = await getToken();
  
      const res = await fetch(`${TOPVIEW_KEY_RUNTIME_BASE}/${keyRow.id}/runtime`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          concurrency_limit: Math.max(
            1,
            parseInt(String(keyRow.concurrency_limit || 1).replace(/\D/g, ""), 10) || 1
          ),
          enabled:
            typeof keyRow.runtime_enabled === "boolean"
              ? keyRow.runtime_enabled
              : true,
          health_status: keyRow.health_status || "healthy",
        }),
      });
  
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.error || "Failed to save queue runtime");
  
      await load();
    } catch (e) {
      setErr(e.message || "Failed to save queue runtime");
    } finally {
      setSaving(false);
    }
  };
  const formatLastChecked = (ts) => {
    if (!ts) return null;
    try {
      const d = new Date(ts);
      if (Number.isNaN(d.getTime())) return null;
      return d.toLocaleString();
    } catch {
      return null;
    }
  };

  const currentPhotFreqLabel = useMemo(() => {
    const choice = minutesToChoice(vPhot?.frequency_minutes ?? 720);
    const preset = FREQ_PRESETS.find((p) => p.id === choice);
    return preset ? preset.label : "Every 12 hours";
  }, [vPhot?.frequency_minutes, minutesToChoice, FREQ_PRESETS]);

  const currentTopviewFreqLabel = useMemo(() => {
    const choice = minutesToChoice(vTopview?.frequency_minutes ?? 720);
    const preset = FREQ_PRESETS.find((p) => p.id === choice);
    return preset ? preset.label : "Every 12 hours";
  }, [vTopview?.frequency_minutes, minutesToChoice, FREQ_PRESETS]);

  const currentPlacidFreqLabel = useMemo(() => {
    const choice = minutesToChoice(vPlacidVendor?.frequency_minutes ?? 720);
    const preset = FREQ_PRESETS.find((p) => p.id === choice);
    return preset ? preset.label : "Every 12 hours";
  }, [vPlacidVendor?.frequency_minutes, minutesToChoice, FREQ_PRESETS]);

  const currentSendgridFreqLabel = useMemo(() => {
    const choice = minutesToChoice(vSendgrid?.frequency_minutes ?? 720);
    const preset = FREQ_PRESETS.find((p) => p.id === choice);
    return preset ? preset.label : "Every 12 hours";
  }, [vSendgrid?.frequency_minutes, minutesToChoice, FREQ_PRESETS]);

  const currentCloudinaryFreqLabel = useMemo(() => {
    const choice = minutesToChoice(vCloudinary?.frequency_minutes ?? 720);
    const preset = FREQ_PRESETS.find((p) => p.id === choice);
    return preset ? preset.label : "Every 12 hours";
  }, [vCloudinary?.frequency_minutes, minutesToChoice, FREQ_PRESETS]);

  const handleAddRecipient = () => {
    const email = newEmail.trim();
    if (!email) return;
    setRecipients((prev) => {
      const existing = Array.isArray(prev.to) ? prev.to : [];
      if (existing.includes(email)) return prev;
      return { ...prev, to: [...existing, email] };
    });
    setNewEmail("");
  };

  const handleRemoveRecipient = (email) => {
    setRecipients((prev) => ({
      ...prev,
      to: (prev.to || []).filter((e) => e !== email),
    }));
  };

  const saveRecipients = async () => {
    setSaving(true);
    setErr("");
    try {
      const token = await getToken();
      const res = await fetch(`${ALERTS_API_BASE}/api/admin/alerts/recipients`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          from: recipients.from,
          to: recipients.to || [],
          cc: recipients.cc || [],
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Failed to save recipients");
      setRecipients({
        from: json.recipients.from,
        to: json.recipients.to || [],
        cc: json.recipients.cc || [],
      });
      // eslint-disable-next-line no-alert
      alert("Recipients saved.");
    } catch (e) {
      setErr(e.message || "Failed to save recipients");
    } finally {
      setSaving(false);
    }
  };

  const badge = (kind, label) => {
    const map = {
      disabled: "bg-rose-600/20 text-rose-300 border border-rose-700/40",
      enabled: "bg-emerald-600/10 text-emerald-300 border border-emerald-700/40",
      neutral: "bg-zinc-800/60 text-zinc-200 border border-zinc-700/60",
    };
    return (
      <span className={`text-[11px] px-2 py-0.5 rounded-full ${map[kind] || map.neutral}`}>
        {label}
      </span>
    );
  };

  const Field = ({ label, children }) => (
    <div className="flex flex-col gap-1">
      <div className="text-[11px] text-zinc-400">{label}</div>
      {children}
    </div>
  );

  const VendorCard = ({
    title,
    subtitle,
    v,
    setV,
    freqLabel,
    onSave,
    onTest,
    extraThresholdBlock,
  }) => {
    const enabled = v?.enabled !== false;

    return (
      <div className="rounded-xl border border-zinc-800/60 bg-zinc-950/30 p-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-medium text-zinc-100">{title}</div>
            <div className="text-[11px] text-zinc-400">{subtitle}</div>
          </div>
          <div className="shrink-0">
            {enabled ? badge("enabled", "Enabled") : badge("disabled", "Disabled")}
          </div>
        </div>

        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Status">
            <label className="flex items-center gap-2 text-[12px] text-zinc-200">
              <input
                type="checkbox"
                checked={!!v?.enabled}
                onChange={(e) => setV((cur) => ({ ...(cur || {}), enabled: e.target.checked }))}
              />
              Enable cronjob
            </label>
          </Field>

          <Field label="Remaining credits">
            {v?.remaining_credits != null ? (
              <div className="flex flex-col gap-1">
                <span className="px-2 py-0.5 rounded-full bg-zinc-900/80 border border-zinc-700/60 text-zinc-100 inline-flex items-center gap-1 w-fit">
                  {v.remaining_credits}
                  {v.credits_cap ? ` / ${v.credits_cap}` : ""} credits
                </span>
                {formatLastChecked(v?.last_checked_at) ? (
                  <span className="text-[10px] text-zinc-500">
                    Last check: {formatLastChecked(v?.last_checked_at)}
                  </span>
                ) : null}
              </div>
            ) : (
              <span className="text-zinc-500 text-xs">No recent check</span>
            )}
          </Field>

          <Field label="Threshold & limits">
            {extraThresholdBlock || (
              <div className="flex items-center gap-2">
                <span className="text-xs text-zinc-400">Alert at</span>
                <input
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="w-24 px-2 py-1 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60"
                  value={v?.threshold_count ?? 20}
                  onChange={(e) => {
                    const n = parseInt(String(e.target.value).replace(/\D/g, ""), 10);
                    setV((cur) => ({
                      ...(cur || {}),
                      threshold_count: Number.isFinite(n) ? n : 0,
                    }));
                  }}
                />
              </div>
            )}
          </Field>

          <Field label="Check frequency">
            <div className="flex flex-col gap-1">
              <select
                className="px-2 py-1 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60"
                value={minutesToChoice(v?.frequency_minutes ?? 720)}
                onChange={(e) => {
                  const mins = choiceToMinutes(e.target.value);
                  setV((cur) => ({ ...(cur || {}), frequency_minutes: mins }));
                }}
              >
                {FREQ_PRESETS.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
              <div className="text-[10px] text-zinc-500">Currently: {freqLabel}</div>
            </div>
          </Field>
        </div>

        <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2">
          <button
            onClick={onSave}
            disabled={saving || loading}
            className={`px-3 py-2 rounded-md border border-emerald-700/50 text-emerald-300 hover:border-emerald-500 text-sm ${
              saving ? "opacity-60 cursor-wait" : ""
            }`}
          >
            Save
          </button>

          <button
            onClick={onTest}
            disabled={saving || loading}
            className="px-3 py-2 rounded-md border border-zinc-700/60 text-zinc-200 hover:border-pink-600/60 hover:text-pink-300 text-sm"
          >
            Send test email
          </button>
        </div>
      </div>
    );
  };

  const placidThresholdBlock = (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="text-xs text-zinc-400">Alert at</span>
        <input
          inputMode="numeric"
          pattern="[0-9]*"
          className="w-24 px-2 py-1 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60"
          value={vPlacidVendor?.threshold_count ?? 20}
          onChange={(e) => {
            const n = parseInt(String(e.target.value).replace(/\D/g, ""), 10);
            setVPlacidVendor((cur) => ({
              ...(cur || {}),
              threshold_count: Number.isFinite(n) ? n : 0,
            }));
          }}
        />
      </div>

      <div className="flex items-center gap-2">
        <span className="text-xs text-zinc-400">Monthly limit</span>
        <input
          inputMode="numeric"
          pattern="[0-9]*"
          className="w-28 px-2 py-1 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60"
          placeholder="e.g. 500"
          value={vPlacidVendor?.monthly_limit != null ? String(vPlacidVendor.monthly_limit) : ""}
          onChange={(e) => {
            const raw = String(e.target.value);
            const cleaned = raw.replace(/\D/g, "");
            const n = parseInt(cleaned || "0", 10);
            setVPlacidVendor((cur) => {
              const nextLimit = Number.isFinite(n) ? n : 0;
              return {
                ...(cur || {}),
                monthly_limit: nextLimit,
                credits_cap: nextLimit || cur?.credits_cap || null,
              };
            });
          }}
        />
      </div>
    </div>
  );

  return (
    <div
      className="rounded-xl mb-3"
      style={{ background: ALERTS_CARD, border: `1px solid ${ALERTS_BORDER}` }}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 text-zinc-200 hover:text-pink-300"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium">Alerts & Integrations</span>
          {cfg?.enabled === false ? badge("disabled", "Disabled") : null}
          {loading ? <span className="text-xs text-zinc-500">Loading…</span> : null}
        </div>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {open ? (
        <div className="px-3 pb-3">
          {err ? <div className="mb-2 text-sm text-amber-300">{err}</div> : null}

          {/* ================== Integrations (mobile cards) ================== */}
          <div className="rounded-lg border border-zinc-800/60 p-3 mb-3">
            <div className="font-medium text-zinc-200 mb-2">Integrations</div>

            {/* Mobile */}
            <div className="grid gap-3 md:hidden">
              <VendorCard
                title="Phot.AI"
                subtitle="Image credits (Phot.AI API)"
                v={vPhot}
                setV={setVPhot}
                freqLabel={currentPhotFreqLabel}
                onSave={savePhot}
                onTest={sendPhotTest}
              />

              <VendorCard
                title="TopView.ai"
                subtitle="Video / image credits (TopView API)"
                v={vTopview}
                setV={setVTopview}
                freqLabel={currentTopviewFreqLabel}
                onSave={saveTopview}
                onTest={sendTopviewTest}
              />

              <VendorCard
                title="Placid manual credits"
                subtitle="Ad creatives (Placid templates)"
                v={vPlacidVendor}
                setV={setVPlacidVendor}
                freqLabel={currentPlacidFreqLabel}
                onSave={savePlacidVendor}
                onTest={sendPlacidTest}
                extraThresholdBlock={placidThresholdBlock}
              />

              <VendorCard
                title="SendGrid"
                subtitle="Email credits balance (SendGrid API)"
                v={vSendgrid}
                setV={setVSendgrid}
                freqLabel={currentSendgridFreqLabel}
                onSave={saveSendgrid}
                onTest={sendSendgridTest}
              />

              <VendorCard
                title="Cloudinary"
                subtitle="Credits usage (Cloudinary Admin API)"
                v={vCloudinary}
                setV={setVCloudinary}
                freqLabel={currentCloudinaryFreqLabel}
                onSave={saveCloudinary}
                onTest={sendCloudinaryTest}
              />
            </div>

            {/* Desktop table (unchanged structure) */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-xs border border-zinc-800/60 rounded-lg overflow-hidden">
                <thead className="text-zinc-300" style={{ background: "rgba(255,255,255,0.04)" }}>
                  <tr>
                    <th className="text-left px-3 py-2">Integration</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-left px-3 py-2">Remaining credits</th>
                    <th className="text-left px-3 py-2">Threshold & limits</th>
                    <th className="text-left px-3 py-2">Check frequency</th>
                    <th className="text-left px-3 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody className="text-zinc-200">
                  {/* Phot.AI row */}
                  <tr className="border-t border-zinc-800/60">
                    <td className="px-3 py-2 align-top">
                      <div className="font-medium text-zinc-100">Phot.AI</div>
                      <div className="text-[11px] text-zinc-400">Image credits (Phot.AI API)</div>
                    </td>

                    <td className="px-3 py-2 align-top">
                      <div className="flex flex-col gap-1">
                        <div className="flex flex-wrap gap-1">
                          {vPhot?.enabled === false
                            ? badge("disabled", "Disabled")
                            : badge("enabled", "Enabled")}
                        </div>
                        <div className="mt-1 flex flex-col gap-1">
                          <label className="flex items-center gap-2 text-[11px] text-zinc-200">
                            <input
                              type="checkbox"
                              checked={!!vPhot?.enabled}
                              onChange={(e) =>
                                setVPhot((v) => ({ ...(v || {}), enabled: e.target.checked }))
                              }
                            />
                            Enable Phot.AI cronjob
                          </label>
                        </div>
                      </div>
                    </td>

                    <td className="px-3 py-2 align-top">
                      {vPhot?.remaining_credits != null ? (
                        <div className="flex flex-col gap-1">
                          <span className="px-2 py-0.5 rounded-full bg-zinc-900/80 border border-zinc-700/60 text-zinc-100 inline-flex items-center gap-1">
                            {vPhot.remaining_credits}
                            {vPhot.credits_cap ? ` / ${vPhot.credits_cap}` : ""} credits
                          </span>
                          {formatLastChecked(vPhot?.last_checked_at) ? (
                            <span className="text-[10px] text-zinc-500">
                              Last check: {formatLastChecked(vPhot?.last_checked_at)}
                            </span>
                          ) : null}
                        </div>
                      ) : (
                        <span className="text-zinc-500 text-xs">No recent check</span>
                      )}
                    </td>

                    <td className="px-3 py-2 align-top">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-zinc-400">Alert at</span>
                        <input
                          inputMode="numeric"
                          pattern="[0-9]*"
                          className="w-24 px-2 py-1 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60"
                          value={vPhot?.threshold_count ?? 20}
                          onChange={(e) => {
                            const n = parseInt(String(e.target.value).replace(/\D/g, ""), 10);
                            setVPhot((v) => ({
                              ...(v || {}),
                              threshold_count: Number.isFinite(n) ? n : 0,
                            }));
                          }}
                        />
                      </div>
                    </td>

                    <td className="px-3 py-2 align-top">
                      <div className="flex flex-col gap-1">
                        <select
                          className="px-2 py-1 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60"
                          value={minutesToChoice(vPhot?.frequency_minutes ?? 720)}
                          onChange={(e) => {
                            const mins = choiceToMinutes(e.target.value);
                            setVPhot((v) => ({ ...(v || {}), frequency_minutes: mins }));
                          }}
                        >
                          {FREQ_PRESETS.map((p2) => (
                            <option key={p2.id} value={p2.id}>
                              {p2.label}
                            </option>
                          ))}
                        </select>
                        <div className="text-[10px] text-zinc-500">
                          Currently: {currentPhotFreqLabel}
                        </div>
                      </div>
                    </td>

                    <td className="px-3 py-2 align-top">
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={savePhot}
                          disabled={saving || loading}
                          className={`px-3 py-1.5 rounded-md border border-emerald-700/50 text-emerald-300 hover:border-emerald-500 ${
                            saving ? "opacity-60 cursor-wait" : ""
                          }`}
                        >
                          Save Phot.AI
                        </button>

                        <button
                          onClick={sendPhotTest}
                          disabled={saving || loading}
                          className="px-3 py-1.5 rounded-md border border-zinc-700/60 text-zinc-200 hover:border-pink-600/60 hover:text-pink-300"
                        >
                          Send Phot.AI test email
                        </button>
                      </div>
                    </td>
                  </tr>

                  {/* TopView row */}
                  <tr className="border-t border-zinc-800/60">
                    <td className="px-3 py-2 align-top">
                      <div className="font-medium text-zinc-100">TopView.ai</div>
                      <div className="text-[11px] text-zinc-400">
                        Video / image credits (TopView API)
                      </div>
                    </td>

                    <td className="px-3 py-2 align-top">
                      <div className="flex flex-col gap-1">
                        <div className="flex flex-wrap gap-1">
                          {vTopview?.enabled === false
                            ? badge("disabled", "Disabled")
                            : badge("enabled", "Enabled")}
                        </div>
                        <div className="mt-1 flex flex-col gap-1">
                          <label className="flex items-center gap-2 text-[11px] text-zinc-200">
                            <input
                              type="checkbox"
                              checked={!!vTopview?.enabled}
                              onChange={(e) =>
                                setVTopview((v) => ({ ...(v || {}), enabled: e.target.checked }))
                              }
                            />
                            Enable TopView cronjob
                          </label>
                        </div>
                      </div>
                    </td>

                    <td className="px-3 py-2 align-top">
                      {vTopview?.remaining_credits != null ? (
                        <div className="flex flex-col gap-1">
                          <span className="px-2 py-0.5 rounded-full bg-zinc-900/80 border border-zinc-700/60 text-zinc-100 inline-flex items-center gap-1">
                            {vTopview.remaining_credits}
                            {vTopview.credits_cap ? ` / ${vTopview.credits_cap}` : ""} credits
                          </span>
                          {formatLastChecked(vTopview?.last_checked_at) ? (
                            <span className="text-[10px] text-zinc-500">
                              Last check: {formatLastChecked(vTopview?.last_checked_at)}
                            </span>
                          ) : null}
                        </div>
                      ) : (
                        <span className="text-zinc-500 text-xs">No recent check</span>
                      )}
                    </td>

                    <td className="px-3 py-2 align-top">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-zinc-400">Alert at</span>
                        <input
                          inputMode="numeric"
                          pattern="[0-9]*"
                          className="w-24 px-2 py-1 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60"
                          value={vTopview?.threshold_count ?? 20}
                          onChange={(e) => {
                            const n = parseInt(String(e.target.value).replace(/\D/g, ""), 10);
                            setVTopview((v) => ({
                              ...(v || {}),
                              threshold_count: Number.isFinite(n) ? n : 0,
                            }));
                          }}
                        />
                      </div>
                    </td>

                    <td className="px-3 py-2 align-top">
                      <div className="flex flex-col gap-1">
                        <select
                          className="px-2 py-1 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60"
                          value={minutesToChoice(vTopview?.frequency_minutes ?? 720)}
                          onChange={(e) => {
                            const mins = choiceToMinutes(e.target.value);
                            setVTopview((v) => ({ ...(v || {}), frequency_minutes: mins }));
                          }}
                        >
                          {FREQ_PRESETS.map((p2) => (
                            <option key={p2.id} value={p2.id}>
                              {p2.label}
                            </option>
                          ))}
                        </select>
                        <div className="text-[10px] text-zinc-500">
                          Currently: {currentTopviewFreqLabel}
                        </div>
                      </div>
                    </td>

                    <td className="px-3 py-2 align-top">
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={saveTopview}
                          disabled={saving || loading}
                          className={`px-3 py-1.5 rounded-md border border-emerald-700/50 text-emerald-300 hover:border-emerald-500 ${
                            saving ? "opacity-60 cursor-wait" : ""
                          }`}
                        >
                          Save TopView
                        </button>

                        <button
                          onClick={sendTopviewTest}
                          disabled={saving || loading}
                          className="px-3 py-1.5 rounded-md border border-zinc-700/60 text-zinc-200 hover:border-pink-600/60 hover:text-pink-300"
                        >
                          Send TopView test email
                        </button>
                      </div>
                    </td>
                  </tr>

                  {/* Placid row */}
                  <tr className="border-t border-zinc-800/60">
                    <td className="px-3 py-2 align-top">
                      <div className="font-medium text-zinc-100">Placid manual credits</div>
                      <div className="text-[11px] text-zinc-400">
                        Ad creatives (Placid templates)
                      </div>
                    </td>

                    <td className="px-3 py-2 align-top">
                      <div className="flex flex-col gap-1">
                        <div className="flex flex-wrap gap-1">
                          {vPlacidVendor?.enabled === false
                            ? badge("disabled", "Disabled")
                            : badge("enabled", "Enabled")}
                        </div>
                        <div className="mt-1 flex flex-col gap-1">
                          <label className="flex items-center gap-2 text-[11px] text-zinc-200">
                            <input
                              type="checkbox"
                              checked={!!vPlacidVendor?.enabled}
                              onChange={(e) =>
                                setVPlacidVendor((v) => ({
                                  ...(v || {}),
                                  enabled: e.target.checked,
                                }))
                              }
                            />
                            Enable Placid cronjob
                          </label>
                        </div>
                      </div>
                    </td>

                    <td className="px-3 py-2 align-top">
                      {vPlacidVendor?.remaining_credits != null ? (
                        <div className="flex flex-col gap-1">
                          <span className="px-2 py-0.5 rounded-full bg-zinc-900/80 border border-zinc-700/60 text-zinc-100 inline-flex items-center gap-1">
                            {vPlacidVendor.remaining_credits}
                            {vPlacidVendor.credits_cap ? ` / ${vPlacidVendor.credits_cap}` : ""} credits
                          </span>
                          {formatLastChecked(vPlacidVendor?.last_checked_at) ? (
                            <span className="text-[10px] text-zinc-500">
                              Last check: {formatLastChecked(vPlacidVendor?.last_checked_at)}
                            </span>
                          ) : null}
                        </div>
                      ) : (
                        <span className="text-zinc-500 text-xs">No recent check</span>
                      )}
                    </td>

                    <td className="px-3 py-2 align-top">
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-zinc-400">Alert at</span>
                          <input
                            inputMode="numeric"
                            pattern="[0-9]*"
                            className="w-24 px-2 py-1 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60"
                            value={vPlacidVendor?.threshold_count ?? 20}
                            onChange={(e) => {
                              const n = parseInt(String(e.target.value).replace(/\D/g, ""), 10);
                              setVPlacidVendor((v) => ({
                                ...(v || {}),
                                threshold_count: Number.isFinite(n) ? n : 0,
                              }));
                            }}
                          />
                        </div>

                        <div className="flex items-center gap-2">
                          <span className="text-xs text-zinc-400">Monthly limit</span>
                          <input
                            inputMode="numeric"
                            pattern="[0-9]*"
                            className="w-28 px-2 py-1 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60"
                            placeholder="e.g. 500"
                            value={
                              vPlacidVendor?.monthly_limit != null
                                ? String(vPlacidVendor.monthly_limit)
                                : ""
                            }
                            onChange={(e) => {
                              const raw = String(e.target.value);
                              const cleaned = raw.replace(/\D/g, "");
                              const n = parseInt(cleaned || "0", 10);
                              setVPlacidVendor((v) => {
                                const nextLimit = Number.isFinite(n) ? n : 0;
                                return {
                                  ...(v || {}),
                                  monthly_limit: nextLimit,
                                  credits_cap: nextLimit || v?.credits_cap || null,
                                };
                              });
                            }}
                          />
                        </div>
                      </div>
                    </td>

                    <td className="px-3 py-2 align-top">
                      <div className="flex flex-col gap-1">
                        <select
                          className="px-2 py-1 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60"
                          value={minutesToChoice(vPlacidVendor?.frequency_minutes ?? 720)}
                          onChange={(e) => {
                            const mins = choiceToMinutes(e.target.value);
                            setVPlacidVendor((v) => ({ ...(v || {}), frequency_minutes: mins }));
                          }}
                        >
                          {FREQ_PRESETS.map((p2) => (
                            <option key={p2.id} value={p2.id}>
                              {p2.label}
                            </option>
                          ))}
                        </select>
                        <div className="text-[10px] text-zinc-500">
                          Currently: {currentPlacidFreqLabel}
                        </div>
                      </div>
                    </td>

                    <td className="px-3 py-2 align-top">
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={savePlacidVendor}
                          disabled={saving || loading}
                          className={`px-3 py-1.5 rounded-md border border-emerald-700/50 text-emerald-300 hover:border-emerald-500 ${
                            saving ? "opacity-60 cursor-wait" : ""
                          }`}
                        >
                          Save Placid
                        </button>

                        <button
                          onClick={sendPlacidTest}
                          disabled={saving || loading}
                          className="px-3 py-1.5 rounded-md border border-zinc-700/60 text-zinc-200 hover:border-pink-600/60 hover:text-pink-300"
                        >
                          Send Placid test email
                        </button>
                      </div>
                    </td>
                  </tr>

                  {/* SendGrid row */}
                  <tr className="border-t border-zinc-800/60">
                    <td className="px-3 py-2 align-top">
                      <div className="font-medium text-zinc-100">SendGrid</div>
                      <div className="text-[11px] text-zinc-400">
                        Email credits balance (SendGrid API)
                      </div>
                    </td>

                    <td className="px-3 py-2 align-top">
                      <div className="flex flex-col gap-1">
                        <div className="flex flex-wrap gap-1">
                          {vSendgrid?.enabled === false
                            ? badge("disabled", "Disabled")
                            : badge("enabled", "Enabled")}
                        </div>
                        <div className="mt-1 flex flex-col gap-1">
                          <label className="flex items-center gap-2 text-[11px] text-zinc-200">
                            <input
                              type="checkbox"
                              checked={!!vSendgrid?.enabled}
                              onChange={(e) =>
                                setVSendgrid((v) => ({ ...(v || {}), enabled: e.target.checked }))
                              }
                            />
                            Enable SendGrid cronjob
                          </label>
                        </div>
                      </div>
                    </td>

                    <td className="px-3 py-2 align-top">
                      {vSendgrid?.remaining_credits != null ? (
                        <div className="flex flex-col gap-1">
                          <span className="px-2 py-0.5 rounded-full bg-zinc-900/80 border border-zinc-700/60 text-zinc-100 inline-flex items-center gap-1">
                            {vSendgrid.remaining_credits}
                            {vSendgrid.credits_cap ? ` / ${vSendgrid.credits_cap}` : ""} credits
                          </span>
                          {formatLastChecked(vSendgrid?.last_checked_at) ? (
                            <span className="text-[10px] text-zinc-500">
                              Last check: {formatLastChecked(vSendgrid?.last_checked_at)}
                            </span>
                          ) : null}
                        </div>
                      ) : (
                        <span className="text-zinc-500 text-xs">No recent check</span>
                      )}
                    </td>

                    <td className="px-3 py-2 align-top">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-zinc-400">Alert at</span>
                        <input
                          inputMode="numeric"
                          pattern="[0-9]*"
                          className="w-24 px-2 py-1 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60"
                          value={vSendgrid?.threshold_count ?? 200}
                          onChange={(e) => {
                            const n = parseInt(String(e.target.value).replace(/\D/g, ""), 10);
                            setVSendgrid((v) => ({
                              ...(v || {}),
                              threshold_count: Number.isFinite(n) ? n : 0,
                            }));
                          }}
                        />
                      </div>
                    </td>

                    <td className="px-3 py-2 align-top">
                      <div className="flex flex-col gap-1">
                        <select
                          className="px-2 py-1 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60"
                          value={minutesToChoice(vSendgrid?.frequency_minutes ?? 720)}
                          onChange={(e) => {
                            const mins = choiceToMinutes(e.target.value);
                            setVSendgrid((v) => ({ ...(v || {}), frequency_minutes: mins }));
                          }}
                        >
                          {FREQ_PRESETS.map((p2) => (
                            <option key={p2.id} value={p2.id}>
                              {p2.label}
                            </option>
                          ))}
                        </select>
                        <div className="text-[10px] text-zinc-500">
                          Currently: {currentSendgridFreqLabel}
                        </div>
                      </div>
                    </td>

                    <td className="px-3 py-2 align-top">
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={saveSendgrid}
                          disabled={saving || loading}
                          className={`px-3 py-1.5 rounded-md border border-emerald-700/50 text-emerald-300 hover:border-emerald-500 ${
                            saving ? "opacity-60 cursor-wait" : ""
                          }`}
                        >
                          Save SendGrid
                        </button>

                        <button
                          onClick={sendSendgridTest}
                          disabled={saving || loading}
                          className="px-3 py-1.5 rounded-md border border-zinc-700/60 text-zinc-200 hover:border-pink-600/60 hover:text-pink-300"
                        >
                          Send SendGrid test email
                        </button>
                      </div>
                    </td>
                  </tr>

                  {/* Cloudinary row */}
                  <tr className="border-t border-zinc-800/60">
                    <td className="px-3 py-2 align-top">
                      <div className="font-medium text-zinc-100">Cloudinary</div>
                      <div className="text-[11px] text-zinc-400">
                        Credits usage (Cloudinary Admin API)
                      </div>
                    </td>

                    <td className="px-3 py-2 align-top">
                      <div className="flex flex-col gap-1">
                        <div className="flex flex-wrap gap-1">
                          {vCloudinary?.enabled === false
                            ? badge("disabled", "Disabled")
                            : badge("enabled", "Enabled")}
                        </div>
                        <div className="mt-1 flex flex-col gap-1">
                          <label className="flex items-center gap-2 text-[11px] text-zinc-200">
                            <input
                              type="checkbox"
                              checked={!!vCloudinary?.enabled}
                              onChange={(e) =>
                                setVCloudinary((v) => ({ ...(v || {}), enabled: e.target.checked }))
                              }
                            />
                            Enable Cloudinary cronjob
                          </label>
                        </div>
                      </div>
                    </td>

                    <td className="px-3 py-2 align-top">
                      {vCloudinary?.remaining_credits != null ? (
                        <div className="flex flex-col gap-1">
                          <span className="px-2 py-0.5 rounded-full bg-zinc-900/80 border border-zinc-700/60 text-zinc-100 inline-flex items-center gap-1">
                            {vCloudinary.remaining_credits}
                            {vCloudinary.credits_cap ? ` / ${vCloudinary.credits_cap}` : ""} credits
                          </span>
                          {formatLastChecked(vCloudinary?.last_checked_at) ? (
                            <span className="text-[10px] text-zinc-500">
                              Last check: {formatLastChecked(vCloudinary?.last_checked_at)}
                            </span>
                          ) : null}
                        </div>
                      ) : (
                        <span className="text-zinc-500 text-xs">No recent check</span>
                      )}
                    </td>

                    <td className="px-3 py-2 align-top">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-zinc-400">Alert at</span>
                        <input
                          inputMode="numeric"
                          pattern="[0-9]*"
                          className="w-24 px-2 py-1 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60"
                          value={vCloudinary?.threshold_count ?? 1000}
                          onChange={(e) => {
                            const n = parseInt(String(e.target.value).replace(/\D/g, ""), 10);
                            setVCloudinary((v) => ({
                              ...(v || {}),
                              threshold_count: Number.isFinite(n) ? n : 0,
                            }));
                          }}
                        />
                      </div>
                    </td>

                    <td className="px-3 py-2 align-top">
                      <div className="flex flex-col gap-1">
                        <select
                          className="px-2 py-1 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60"
                          value={minutesToChoice(vCloudinary?.frequency_minutes ?? 720)}
                          onChange={(e) => {
                            const mins = choiceToMinutes(e.target.value);
                            setVCloudinary((v) => ({ ...(v || {}), frequency_minutes: mins }));
                          }}
                        >
                          {FREQ_PRESETS.map((p2) => (
                            <option key={p2.id} value={p2.id}>
                              {p2.label}
                            </option>
                          ))}
                        </select>
                        <div className="text-[10px] text-zinc-500">
                          Currently: {currentCloudinaryFreqLabel}
                        </div>
                      </div>
                    </td>

                    <td className="px-3 py-2 align-top">
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={saveCloudinary}
                          disabled={saving || loading}
                          className={`px-3 py-1.5 rounded-md border border-emerald-700/50 text-emerald-300 hover:border-emerald-500 ${
                            saving ? "opacity-60 cursor-wait" : ""
                          }`}
                        >
                          Save Cloudinary
                        </button>

                        <button
                          onClick={sendCloudinaryTest}
                          disabled={saving || loading}
                          className="px-3 py-1.5 rounded-md border border-zinc-700/60 text-zinc-200 hover:border-pink-600/60 hover:text-pink-300"
                        >
                          Send Cloudinary test email
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* ================== TopView keys pool ================== */}
          {/* (unchanged below) */}
          <div className="rounded-lg border border-zinc-800/60 p-3 mb-3">
            <div className="font-medium text-zinc-200 mb-1">TopView API keys pool</div>
            <div className="text-xs text-zinc-400 mb-3">
  Manage the TopView accounts used for generation. Each key shows live credits, queue limit,
  active jobs, runtime health, and whether that key is enabled for queue dispatch.
</div>

            {/* Mobile list */}
            <div className="md:hidden">
              {topviewKeys && topviewKeys.length > 0 ? (
                <div className="grid gap-2">
{topviewKeys.map((k) => {
  const uidShort =
    typeof k.uid === "string" && k.uid.length > 12
      ? `${k.uid.slice(0, 8)}…${k.uid.slice(-4)}`
      : k.uid || "—";
  const liveRemain =
    typeof k.live_remaining_credits === "number" ? k.live_remaining_credits : null;
  const maskedKey = k.masked_api_key || "••••";

  return (
    <div
      key={k.id}
      className="rounded-xl border border-zinc-800/60 bg-zinc-950/30 p-3"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-medium text-zinc-100 truncate">
            {k.label || "Unnamed key"}
          </div>
          <div className="text-[11px] text-zinc-400 break-all">UID: {uidShort}</div>
          <div className="text-[11px] text-zinc-400 break-all">Key: {maskedKey}</div>
        </div>

        <label className="flex items-center gap-2 text-[11px] text-zinc-200 shrink-0">
          <input
            type="checkbox"
            checked={!!k.enabled}
            onChange={(e) => toggleTopviewKeyEnabled(k.id, e.target.checked)}
            disabled={saving || loading}
          />
          Enabled
        </label>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2">
        <div className="text-[11px] text-zinc-400">
          Live credits:{" "}
          <span className="text-zinc-200">
            {liveRemain != null ? `${liveRemain}` : "Not available"}
          </span>
        </div>

        <div className="text-[11px] text-zinc-400">
          Active jobs:{" "}
          <span className="text-zinc-200">{Number(k.active_jobs || 0)}</span>
        </div>

        <div>
          <div className="text-[11px] text-zinc-400 mb-1">Queue limit</div>
          <input
            inputMode="numeric"
            pattern="[0-9]*"
            className="w-full px-2 py-1.5 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60 text-xs"
            value={String(k.concurrency_limit ?? 1)}
            onChange={(e) =>
              patchTopviewKeyLocal(k.id, {
                concurrency_limit:
                  parseInt(String(e.target.value).replace(/\D/g, ""), 10) || 1,
              })
            }
          />
        </div>

        <div>
          <div className="text-[11px] text-zinc-400 mb-1">Health</div>
          <select
            className="w-full px-2 py-1.5 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60 text-xs"
            value={String(k.health_status || "healthy")}
            onChange={(e) =>
              patchTopviewKeyLocal(k.id, { health_status: e.target.value })
            }
          >
            <option value="healthy">healthy</option>
            <option value="degraded">degraded</option>
            <option value="disabled">disabled</option>
          </select>
        </div>
      </div>

      <div className="mt-3 flex flex-col gap-2">
        <label className="flex items-center gap-2 text-[11px] text-zinc-200">
          <input
            type="checkbox"
            checked={k.runtime_enabled !== false}
            onChange={(e) =>
              patchTopviewKeyLocal(k.id, { runtime_enabled: e.target.checked })
            }
            disabled={saving || loading}
          />
          Queue enabled
        </label>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => saveTopviewKeyRuntime(k)}
            disabled={saving || loading}
            className="px-2.5 py-1.5 rounded-md border border-emerald-700/50 text-[11px] text-emerald-300 hover:border-emerald-500"
          >
            Save queue config
          </button>

          <button
            type="button"
            onClick={() => deleteTopviewKey(k.id)}
            disabled={saving || loading}
            className="px-2.5 py-1.5 rounded-md border border-zinc-700/60 text-[11px] text-zinc-300 hover:border-rose-500 hover:text-rose-300"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
})}
                </div>
              ) : (
                <div className="text-xs text-zinc-500">No TopView keys configured yet.</div>
              )}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block mb-3 overflow-x-auto">
              {topviewKeys && topviewKeys.length > 0 ? (
                <table className="w-full text-xs border border-zinc-800/60 rounded-lg overflow-hidden">
                  <thead className="text-zinc-300" style={{ background: "rgba(255,255,255,0.04)" }}>
                  <tr>
  <th className="text-left px-3 py-2">Label</th>
  <th className="text-left px-3 py-2">UID</th>
  <th className="text-left px-3 py-2">API key</th>
  <th className="text-left px-3 py-2">Live credits</th>
  <th className="text-left px-3 py-2">Queue limit</th>
  <th className="text-left px-3 py-2">Active jobs</th>
  <th className="text-left px-3 py-2">Queue enabled</th>
  <th className="text-left px-3 py-2">Health</th>
  <th className="text-left px-3 py-2">Enabled</th>
  <th className="text-left px-3 py-2">Actions</th>
</tr>
                  </thead>
                  <tbody className="text-zinc-200">
                    {topviewKeys.map((k) => {
                      const uidShort =
                        typeof k.uid === "string" && k.uid.length > 12
                          ? `${k.uid.slice(0, 8)}…${k.uid.slice(-4)}`
                          : k.uid || "—";

                      const liveRemain =
                        typeof k.live_remaining_credits === "number" ? k.live_remaining_credits : null;

                      const maskedKey = k.masked_api_key || "••••";

                      return (
<tr key={k.id} className="border-t border-zinc-800/60 bg-zinc-950/40">
  <td className="px-3 py-2 align-top">
    <div className="font-medium text-zinc-100">{k.label || "Unnamed key"}</div>
  </td>

  <td className="px-3 py-2 align-top">
    <div className="text-[11px] text-zinc-300">{uidShort}</div>
  </td>

  <td className="px-3 py-2 align-top">
    <div className="text-[11px] text-zinc-300">{maskedKey}</div>
  </td>

  <td className="px-3 py-2 align-top">
    {liveRemain != null ? (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-zinc-900/80 border border-zinc-700/60 text-[11px] text-zinc-100">
        {liveRemain} credits
      </span>
    ) : (
      <span className="text-[11px] text-zinc-500">Not available</span>
    )}
  </td>

  <td className="px-3 py-2 align-top">
    <input
      inputMode="numeric"
      pattern="[0-9]*"
      className="w-20 px-2 py-1 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60 text-xs"
      value={String(k.concurrency_limit ?? 1)}
      onChange={(e) =>
        patchTopviewKeyLocal(k.id, {
          concurrency_limit:
            parseInt(String(e.target.value).replace(/\D/g, ""), 10) || 1,
        })
      }
    />
  </td>

  <td className="px-3 py-2 align-top">
    <span className="text-[11px] text-zinc-200">{Number(k.active_jobs || 0)}</span>
  </td>

  <td className="px-3 py-2 align-top">
    <label className="flex items-center gap-1 text-[11px] text-zinc-200">
      <input
        type="checkbox"
        checked={k.runtime_enabled !== false}
        onChange={(e) =>
          patchTopviewKeyLocal(k.id, { runtime_enabled: e.target.checked })
        }
        disabled={saving || loading}
      />
      Queue enabled
    </label>
  </td>

  <td className="px-3 py-2 align-top">
    <select
      className="px-2 py-1 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60 text-xs"
      value={String(k.health_status || "healthy")}
      onChange={(e) =>
        patchTopviewKeyLocal(k.id, { health_status: e.target.value })
      }
    >
      <option value="healthy">healthy</option>
      <option value="degraded">degraded</option>
      <option value="disabled">disabled</option>
    </select>
  </td>

  <td className="px-3 py-2 align-top">
    <label className="flex items-center gap-1 text-[11px] text-zinc-200">
      <input
        type="checkbox"
        checked={!!k.enabled}
        onChange={(e) => toggleTopviewKeyEnabled(k.id, e.target.checked)}
        disabled={saving || loading}
      />
      Enabled
    </label>
  </td>

  <td className="px-3 py-2 align-top">
    <div className="flex flex-wrap gap-2">
      <button
        type="button"
        onClick={() => saveTopviewKeyRuntime(k)}
        disabled={saving || loading}
        className="px-2 py-1 rounded-md border border-emerald-700/50 text-[11px] text-emerald-300 hover:border-emerald-500"
      >
        Save queue
      </button>

      <button
        type="button"
        onClick={() => deleteTopviewKey(k.id)}
        disabled={saving || loading}
        className="px-2 py-1 rounded-md border border-zinc-700/60 text-[11px] text-zinc-300 hover:border-rose-500 hover:text-rose-300"
      >
        Delete
      </button>
    </div>
  </td>
</tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="text-xs text-zinc-500">No TopView keys configured yet.</div>
              )}
            </div>

            {/* Add key form (responsive) */}
            <div className="border-t border-zinc-800/70 pt-3 mt-2">
              <div className="text-[11px] text-zinc-400 mb-2">Add a new TopView key</div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-2">
                <input
                  className="px-2 py-2 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60 text-xs"
                  placeholder="Label (e.g. Main account)"
                  value={newTopviewKey.label}
                  onChange={(e) => setNewTopviewKey((prev) => ({ ...prev, label: e.target.value }))}
                />
                <input
                  className="px-2 py-2 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60 text-xs"
                  placeholder="TopView UID"
                  value={newTopviewKey.uid}
                  onChange={(e) => setNewTopviewKey((prev) => ({ ...prev, uid: e.target.value }))}
                />
                <input
                  type="password"
                  className="px-2 py-2 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60 text-xs"
                  placeholder="TopView API key"
                  value={newTopviewKey.api_key}
                  onChange={(e) =>
                    setNewTopviewKey((prev) => ({ ...prev, api_key: e.target.value }))
                  }
                />
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <input
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className="w-full sm:w-40 px-2 py-2 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60 text-xs"
                    placeholder="Monthly quota (optional)"
                    value={newTopviewKey.monthly_quota}
                    onChange={(e) => {
                      const raw = String(e.target.value);
                      const cleaned = raw.replace(/\D/g, "");
                      setNewTopviewKey((prev) => ({ ...prev, monthly_quota: cleaned }));
                    }}
                  />
                  <label className="flex items-center gap-2 text-[11px] text-zinc-200">
                    <input
                      type="checkbox"
                      checked={!!newTopviewKey.enabled}
                      onChange={(e) =>
                        setNewTopviewKey((prev) => ({ ...prev, enabled: e.target.checked }))
                      }
                    />
                    Enabled
                  </label>
                </div>
              </div>

              <button
                type="button"
                onClick={addTopviewKey}
                disabled={saving || loading}
                className={`w-full sm:w-auto px-3 py-2 rounded-md border border-pink-600/70 text-xs text-pink-200 hover:border-pink-400 hover:text-pink-100 ${
                  saving ? "opacity-60 cursor-wait" : ""
                }`}
              >
                Add TopView key
              </button>
            </div>
          </div>

          {/* ================== Recipients configuration ================== */}
          {/* (unchanged below) */}
          <div className="rounded-lg border border-zinc-800/60 p-3">
            <div className="font-medium text-zinc-200 mb-2">Alert recipients</div>
            <div className="text-xs text-zinc-400 mb-2">
              All configured email addresses will receive alert emails when a vendor hits its threshold.
            </div>

            <div className="mb-2">
              <div className="text-[11px] text-zinc-400 mb-1">From address</div>
              <div className="px-2 py-1 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60 text-xs inline-block">
                {recipients.from || "contact@pixelads.ai"}
              </div>
            </div>

            <div className="mb-3">
              <div className="text-[11px] text-zinc-400 mb-1">To recipients</div>

              <div className="flex flex-wrap gap-1 mb-2">
                {(recipients.to || []).length === 0 ? (
                  <span className="text-[11px] text-zinc-500">No recipients configured.</span>
                ) : (
                  recipients.to.map((email) => (
                    <span
                      key={email}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-zinc-900 border border-zinc-700/70 text-[11px] text-zinc-100"
                    >
                      <span className="break-all">{email}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveRecipient(email)}
                        className="ml-1 text-zinc-400 hover:text-rose-300 text-[13px]"
                        aria-label="Remove recipient"
                        title="Remove"
                      >
                        ×
                      </button>
                    </span>
                  ))
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                <input
                  className="w-full sm:w-[280px] px-2 py-2 rounded-md bg-zinc-950/70 text-zinc-100 border border-zinc-700/60 text-xs"
                  placeholder="Add email (e.g. you@example.com)"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddRecipient();
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={handleAddRecipient}
                  className="w-full sm:w-auto px-3 py-2 rounded-md border border-zinc-700/60 text-zinc-200 hover:border-pink-600/60 hover:text-pink-300 text-xs"
                  disabled={saving || loading}
                >
                  Add email
                </button>
              </div>
            </div>

            <button
              onClick={saveRecipients}
              disabled={saving || loading}
              className={`w-full sm:w-auto px-3 py-2 rounded-md border border-emerald-700/50 text-emerald-300 hover:border-emerald-500 text-xs ${
                saving ? "opacity-60 cursor-wait" : ""
              }`}
            >
              Save recipients
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default AdminAlertsPanel;
